import { GENERATION_TOOL_LONG_FORM, type GenerationTool, TEXT_UNSCORED_LABEL } from './const';
import { getToolContentType } from './toolProps';
import { isFreestyleOrImproverTool, mapGenerationTool } from './utils';

const LONG_FORM_WRAPPER_TOOLS: string[] = Object.values(GENERATION_TOOL_LONG_FORM);

// A tool's content type names its result, except where it spells the same result two ways:
// `Meta Ad` (the headline and primary text tools) is the `Meta Image Ad` its image tool is named
// after, `Youtube Video` is `YouTube Video`, and a blog is `Blog` in the section tools but
// `Blog post` in the document tool — the label a blog gets is the long-form content type's title.
const RESULT_LABEL_BY_CONTENT_TYPE: Record<string, string> = {
  Blog: 'Blog / Article',
  'Blog post': 'Blog / Article',
  'Meta Ad': 'Meta Image Ad',
  'Youtube Video': 'YouTube Video',
};

const toResultLabel = (contentType: string): string =>
  RESULT_LABEL_BY_CONTENT_TYPE[contentType] ?? contentType;

/**
 * The name of the result a generation tool belongs to — the group name of its parts, so Google Search
 * Ad's headline and description tools are one "Google Search Ad" and the Meta Image Ad's headline,
 * primary text and image tools are one "Meta Image Ad". `undefined` for a tool that names nothing.
 *
 * A long-form document's sections carry the tools its outline picked, so white paper and research
 * report documents can't be told apart from a tool: nothing here maps to either label.
 */
export const getToolResultLabel = (tool: GenerationTool): string | undefined => {
  if (isFreestyleOrImproverTool(tool)) {
    return TEXT_UNSCORED_LABEL;
  }

  // These stand for a whole long-form run, not for what the document holds.
  if (LONG_FORM_WRAPPER_TOOLS.includes(tool)) {
    return undefined;
  }

  const contentType = getToolContentType(mapGenerationTool(tool));

  return contentType ? toResultLabel(contentType) : undefined;
};
